import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { listAllPackages } from "./packageService";
import apiMiddleware from "../../../api/apiMiddleware";
import SubPackages from "./SubPackages";
import PackageSettings from "./PackageSettings";
import Pagination from "../../common/Pagination";
import Toster from "../../common/Toster";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  TableSearchInput,
  TableEmptyRow,
  TableActionButton,
  TableActionsCell,
  ExpandableText,
} from "../../common/adminTable";
import { buildPaginationMeta } from "../../../utils/clientPagination";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";
import StatusBadge, { getApprovalVariant } from "../../common/adminTable/StatusBadge";

import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useTableSearch from "../../../hooks/useTableSearch";

const FILTER_SELECT_CLASS =
  "min-w-[8.5rem] rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-gray-400 focus:outline-none";

const formatTourType = (type) =>
  type
    ?.split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ") || "—";

const Package = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [viewSubPackages, setViewSubPackages] = useState(false);

  const [showStatus, setShowStatus] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [toast, setToast] = useState(null);

  const { searchQuery, setSearchQuery, activeSearch, clearSearch } =
    useTableSearch();

  const [tourTypeFilter, setTourTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [approvalStatusFilter, setApprovalStatusFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("all");

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  // Build filters object — no search param when empty / too short
  const filters = {
    search: activeSearch || undefined,
    tour_type: tourTypeFilter !== "all" ? tourTypeFilter : undefined,
    status: statusFilter !== "all" ? statusFilter : undefined,
    approval_status:
      approvalStatusFilter !== "all" ? approvalStatusFilter : undefined,
    supplier_id: supplierFilter !== "all" ? supplierFilter : undefined,
  };

  const {
    data: response,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "packages",
      currentPage,
      activeSearch,
      tourTypeFilter,
      statusFilter,
      approvalStatusFilter,
      supplierFilter,
    ],
    queryFn: () => listAllPackages(currentPage, filters),
    placeholderData: keepPreviousData,
  });

  // Fetch all tour types for filter dropdown (without filters)
  // Fetch with a larger per_page (100) to get more tour types in one request
  const { data: allTourTypesData } = useQuery({
    queryKey: ["packagesTourTypes"],
    queryFn: () => listAllPackages(1, {}, 100), // Fetch 100 packages to get all tour types
    enabled: true,
  });

  // Fetch all suppliers for filter dropdown
  // Fetch with a larger per_page (100) to get all suppliers in one request
  const { data: suppliersData } = useQuery({
    queryKey: ["allSuppliers"],
    queryFn: async () => {
      const response = await apiMiddleware.get(`admin-suppliers?per_page=100`);
      return response.data.data;
    },
    enabled: true,
  });

  const packages = response?.data?.data || [];
  const uniqueTourTypes = useMemo(() => {
    const types = new Set();
    // Admin API returns packages directly, not groups
    // Response structure: allTourTypesData.data.data (array of packages)
    const dataSource = allTourTypesData?.data?.data || [];
    dataSource.forEach((pkg) => {
      if (pkg.tour_type) {
        types.add(pkg.tour_type);
      }
    });

    return Array.from(types);
  }, [allTourTypesData]);

  const pageMeta = ADMIN_PAGE_META.packages;

  const current_page = response?.data?.current_page || 1;
  const last_page = response?.data?.last_page || 1;
  const total = response?.data?.total || 0;
  const per_page = response?.data?.per_page || 10;

  const pagination = buildPaginationMeta({
    currentPage: current_page,
    lastPage: last_page,
    perPage: per_page,
    total,
  });

  const handlePageChange = (page) => {
    if (page !== currentPage) setCurrentPage(page);
  };

  const clearFilters = () => {
    clearSearch();
    setTourTypeFilter("all");
    setStatusFilter("all");
    setApprovalStatusFilter("all");
    setSupplierFilter("all");
    setCurrentPage(1);
  };

  const hasActiveFilters =
    activeSearch !== "" ||
    tourTypeFilter !== "all" ||
    statusFilter !== "all" ||
    approvalStatusFilter !== "all" ||
    supplierFilter !== "all";

  const view = (id) => {
    navigate(`/packages/${id}/edit`);
  };

  const openStatus = (pkg) => {
    setSelectedPackage(pkg);
    setShowStatus(true);
  };

  return (
    <AdminPageLayout>
      <AdminPageHeader title={pageMeta.title} subtitle={pageMeta.subtitle} />

      <AdminPageCard>
        <AdminPageCardToolbar className="border-b border-gray-100 bg-white px-4 py-3 sm:px-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <TableSearchInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or ID..."
                className="w-full sm:max-w-[260px]"
              />
              <select
                value={tourTypeFilter}
                onChange={(e) => {
                  setTourTypeFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className={FILTER_SELECT_CLASS}
                aria-label="Tour type"
              >
                <option value="all">All types</option>
                {uniqueTourTypes.map((type) => (
                  <option key={type} value={type}>
                    {formatTourType(type)}
                  </option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className={FILTER_SELECT_CLASS}
                aria-label="Status"
              >
                <option value="all">All status</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
              <select
                value={approvalStatusFilter}
                onChange={(e) => {
                  setApprovalStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className={FILTER_SELECT_CLASS}
                aria-label="Approval"
              >
                <option value="all">All approval</option>
                <option value="1">Approved</option>
                <option value="0">Pending</option>
                <option value="2">Rejected</option>
              </select>
              <select
                value={supplierFilter}
                onChange={(e) => {
                  setSupplierFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className={`${FILTER_SELECT_CLASS} sm:min-w-[10rem]`}
                aria-label="Supplier"
              >
                <option value="all">All suppliers</option>
                {suppliersData?.data?.map((supplier) => (
                  <option key={supplier.user_id} value={supplier.user_id}>
                    {supplier.company_name || supplier.user?.name || "Unknown"}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span>
                <span className="font-semibold text-gray-900">{total}</span>{" "}
                {total === 1 ? "package" : "packages"}
              </span>
              {hasActiveFilters ? (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="font-medium text-gray-600 transition hover:text-gray-900"
                >
                  Clear
                </button>
              ) : null}
            </div>
          </div>
        </AdminPageCardToolbar>

        {isLoading ? (
          <div className="flex items-center justify-center px-6 py-16 text-sm text-gray-500">
            Loading packages...
          </div>
        ) : error ? (
          <div className="p-6 text-red-600">Error: {error.message}</div>
        ) : (
          <AdminTable
            className="[&_table]:table-fixed"
            footer={
              <AdminTableFooter>
                <Pagination pagination={pagination} onPageChange={handlePageChange} />
              </AdminTableFooter>
            }
          >
            <AdminTableHead>
              <AdminTableTh className="w-[32%]">Package</AdminTableTh>
              <AdminTableTh className="w-[24%]">Supplier</AdminTableTh>
              <AdminTableTh className="w-[12%]">Duration</AdminTableTh>
              <AdminTableTh className="w-[20%]">Status</AdminTableTh>
              <AdminTableTh align="right" className="w-[12%]">
                Actions
              </AdminTableTh>
            </AdminTableHead>
            <AdminTableBody>
              {packages.length === 0 ? (
                <TableEmptyRow
                  colSpan={5}
                  emptyMessage="No packages found"
                  searchQuery={hasActiveFilters ? activeSearch || "filters" : ""}
                  searchMessage="No packages found matching your filters"
                  onClearSearch={hasActiveFilters ? clearFilters : undefined}
                />
              ) : (
                packages.map((pkg) => (
                  <AdminTableRow key={pkg.id}>
                    <AdminTableCell nowrap={false}>
                      <ExpandableText
                        text={pkg.name}
                        label="full package name"
                        className="font-medium text-gray-900"
                      />
                      <span className="mt-0.5 block text-xs text-gray-500">
                        {formatTourType(pkg.tour_type)}
                      </span>
                    </AdminTableCell>
                    <AdminTableCell nowrap={false}>
                      <p className="text-sm text-gray-800">
                        {pkg.user?.supplier_details?.company_name ||
                          pkg.user?.name ||
                          "Unknown"}
                      </p>
                    </AdminTableCell>
                    <AdminTableCell>
                      <span className="text-sm text-gray-700">
                        {pkg.total_days}d · {pkg.total_nights}n
                      </span>
                    </AdminTableCell>
                    <AdminTableCell nowrap={false}>
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusBadge variant={getApprovalVariant(pkg.is_approved)}>
                          {pkg.is_approved === 1
                            ? "Approved"
                            : pkg.is_approved === 2
                              ? "Rejected"
                              : "Pending"}
                        </StatusBadge>
                        {(pkg.popular === 1 || pkg.recommended === 1) && (
                          <span className="text-xs text-gray-400">
                            {[pkg.popular === 1 && "Popular", pkg.recommended === 1 && "Recommended"]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        )}
                      </div>
                    </AdminTableCell>
                    <TableActionsCell>
                      <TableActionButton
                        variant="view"
                        showLabel={false}
                        onClick={() => view(pkg.id)}
                      />
                      <TableActionButton
                        variant="settings"
                        showLabel={false}
                        onClick={() => openStatus(pkg)}
                      />
                    </TableActionsCell>
                  </AdminTableRow>
                ))
              )}
            </AdminTableBody>
          </AdminTable>
        )}
      </AdminPageCard>

      {viewSubPackages && (
        <SubPackages
          open={viewSubPackages}
          onClose={() => setViewSubPackages(false)}
        />
      )}

      {showStatus && (
        <PackageSettings
          open={showStatus}
          onClose={() => {
            setShowStatus(false);
            setSelectedPackage(null);
          }}
          packageData={selectedPackage}
          onNotify={setToast}
        />
      )}

      {toast && (
        <Toster
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </AdminPageLayout>
  );
};

export default Package;
